import { StateCard } from "@/components/patterns";
import { ApplyFromUrl } from "@/lib/openWithConfig";
import { dateLocale } from "@/lib/dateTime";
import { CheckCheck } from "lucide-react";
import { ListEmpty } from "@/components/patterns";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { InlineCell } from "@/components/inline/InlineCell";
import { ListPager } from "@/components/ui/list-pager";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Plus, RefreshCw, Search, Loader2 } from "lucide-react";
import { formatINR, formatINRCompact, cn } from "@/lib/utils";
import { UploadedTag } from "@/components/proposals/UploadedTag";
import { UploadDocumentDialog } from "@/components/proposals/UploadDocumentDialog";
import { QuoteStatusBadge } from "./QuoteStatusBadge";
import type { QuotationRecord } from "@/types";
import { FilterBar } from "@/components/filters/FilterBar";
import { PageHeader, Person, ViewsPanel } from "@/components/patterns";
import { OwnerCell } from "@/components/inline/OwnerCell";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { useRowSelection } from "@/lib/useRowSelection";
import { BulkBar, BulkButton } from "@/components/patterns";

import { downloadCsv, EXPORT_LIMIT, fetchAllRows } from "@/lib/exportCsv";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import {
  CalendarDays as FilterDateIcon,
  ShieldCheck as FilterValidityIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const QUOTE_FILTER_DEFAULTS = { owner: "all", date: "any", validity: "all" };
const VALIDITY_OPTIONS = [
  { value: "all", label: "Any validity" },
  { value: "valid", label: "Still valid" },
  { value: "expiring", label: "Expiring in 7 days" },
  { value: "expired", label: "Expired" },
];

type Tab =
  | "ALL"
  | "MINE_TO_APPROVE"
  | "MINE"
  | "EXPIRING"
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "SENT"
  | "ACCEPTED"
  | "REJECTED"
  | "EXPIRED";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "ALL", label: "All" },
  { id: "MINE_TO_APPROVE", label: "Needs my approval" },
  { id: "MINE", label: "My quotes" },
  { id: "EXPIRING", label: "Expiring this week" },
  { id: "DRAFT", label: "Draft" },
  { id: "PENDING_APPROVAL", label: "Pending approval" },
  { id: "APPROVED", label: "Approved" },
  { id: "SENT", label: "Sent" },
  { id: "ACCEPTED", label: "Accepted" },
  { id: "REJECTED", label: "Rejected" },
  { id: "EXPIRED", label: "Expired" },
];

const formatDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";

const QUOTE_BULK_FIELDS: BulkField[] = [{ field: "valid_until", label: "Valid until", kind: "date" }];

export function QuotesPage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission, user } = useAuth();
  const quoteFilters = usePersistentFilters("quotes", QUOTE_FILTER_DEFAULTS);

  // "+ Add filter" on any field the API offers
  const fieldFilters = useFieldFilters("quotes");
  const sorting = useSort("quotes");
  const [fieldDefs, setFieldDefs] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/Quotation/FilterFields").then((res) => setFieldDefs(unwrapArray<FilterFieldDef>(res))).catch(() => setFieldDefs([]));
  }, []);
  const [quoteOptions, setQuoteOptions] = useState<{ total: number; owners: OwnerFacet[] }>({ total: 0, owners: [] });
  const canApprove = hasPermission("quote.approve");
  const canCreate = hasPermission("quote.create");
  const canEditInline = hasPermission("quote.update");

  const [tab, setTab] = useState<Tab>("ALL");
  const [search, setSearch] = useState("");

  // New quote dialog
  const [newOpen, setNewOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState("");
  const [selectedDealLabel, setSelectedDealLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const list = usePagedList<QuotationRecord>({
    deps: [tab, search, quoteFilters.values, fieldFilters.values, fieldDefs, sorting.sort],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = quoteFilters.values;
      const query = toQuery({
        mine_to_approve: tab === "MINE_TO_APPROVE" ? "true" : undefined,
        status: tab !== "ALL" && tab !== "MINE_TO_APPROVE" && tab !== "MINE" && tab !== "EXPIRING" ? tab : undefined,
        search: search.trim(),
        owner: tab === "MINE" ? "me" : f.owner,
        validity: tab === "EXPIRING" ? "expiring" : f.validity,
        ...dateRangeParams(f.date, "date"),
        f: fieldFilters.param(fieldDefs), sort: sorting.sort,
        page,
        page_size: 30,
      });
      try {
        const [res, optRes] = await Promise.all([
          api.get(`/api/Quotation/List${query}`),
          api.get("/api/Quotation/FilterOptions").catch(() => null),
        ]);
        if (optRes) setQuoteOptions(unwrapObject(optRes));
        return unwrapPage<QuotationRecord>(res);
      } catch (err: any) {
        throw err;
      }
    },
  });
  // The same filters as the list, for the export
  const currentQuery = () => {
    const f = quoteFilters.values;
    return toQuery({
      mine_to_approve: tab === "MINE_TO_APPROVE" ? "true" : undefined,
      status: tab !== "ALL" && tab !== "MINE_TO_APPROVE" && tab !== "MINE" && tab !== "EXPIRING" ? tab : undefined,
      search: search.trim(),
      owner: tab === "MINE" ? "me" : f.owner,
      validity: tab === "EXPIRING" ? "expiring" : f.validity,
      ...dateRangeParams(f.date, "date"),
      f: fieldFilters.param(fieldDefs), sort: sorting.sort,
    });
  };
  const [exporting, setExporting] = useState(false);
  const exportQuotes = async () => {
    setExporting(true);
    try {
      const { rows, total } = await fetchAllRows<QuotationRecord>("/api/Quotation/List", currentQuery());
      downloadCsv(
        `quotes-${new Date().toISOString().slice(0, 10)}.csv`,
        [
          ["Quote", (q) => q.display_number],
          ["Customer", (q) => q.account?.name],
          ["Deal", (q) => q.deal?.name],
          ["Owner", (q) => (q.owner ? `${q.owner.first_name} ${q.owner.last_name || ""}`.trim() : "")],
          ["Date", (q) => q.quote_date?.slice(0, 10)],
          ["Valid until", (q) => q.valid_until?.slice(0, 10)],
          ["Grand total", (q) => q.grand_total],
          ["Status", (q) => q.quote_status],
        ],
        rows,
      );
      if (total > rows.length) error("Export is partial", `The first ${EXPORT_LIMIT} of ${total} quotes were saved. Narrow the filters to get the rest.`);
    } catch (e: any) {
      error("Could not export", e?.message || "Try again");
    } finally {
      setExporting(false);
    }
  };

  const quotes = list.rows;
  const sel = useRowSelection(quotes.map((q) => q.id));
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const loading = list.loading;
  const loadQuotes = list.reload;

  // Counts beside each view in the panel
  const [counts, setCounts] = useState<Record<string, number>>({});
  // The biggest quote waiting for this person, for the banner
  const [biggest, setBiggest] = useState<QuotationRecord | null>(null);
  useEffect(() => {
    if (!canApprove || !((counts.MINE_TO_APPROVE ?? 0) > 0)) {
      setBiggest(null);
      return;
    }
    api
      .get("/api/Quotation/List?mine_to_approve=true&page_size=50")
      .then((r) => setBiggest(unwrapPage<QuotationRecord>(r).rows.sort((a, b) => Number(b.grand_total) - Number(a.grand_total))[0] ?? null))
      .catch(() => setBiggest(null));
  }, [canApprove, counts.MINE_TO_APPROVE]);

  const [stats, setStats] = useState<{ sent_amount: number; accepted_amount: number; acceptance_rate: number | null } | null>(null);
  useEffect(() => {
    api
      .get("/api/Quotation/Stats")
      .then((r) => setStats(unwrapObject(r)))
      .catch(() => setStats(null));
  }, [list.total]);
  useEffect(() => {
    const keys = TABS.filter((t) => t.id !== "MINE_TO_APPROVE" || canApprove).map((t) => t.id);
    Promise.all(
      keys.map((k) => {
        const q = toQuery({
          mine_to_approve: k === "MINE_TO_APPROVE" ? "true" : undefined,
          status: k !== "ALL" && k !== "MINE_TO_APPROVE" && k !== "MINE" && k !== "EXPIRING" ? k : undefined,
          owner: k === "MINE" ? "me" : undefined,
          validity: k === "EXPIRING" ? "expiring" : undefined,
          page: 1,
          page_size: 1,
        });
        return api
          .get(`/api/Quotation/List${q}`)
          .then((r) => [k, unwrapPage(r).total] as const)
          .catch(() => [k, 0] as const);
      }),
    ).then((rows) => setCounts(Object.fromEntries(rows)));
  }, [canApprove, list.total]);

  const openNewQuote = () => {
    setSelectedDeal("");
    setSelectedDealLabel("");
    setNewOpen(true);
  };

  // "+ Create → New quote" in the app frame links here with ?create=true
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("create") !== "true") return;
    openNewQuote();
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeal) {
      error("Select a Deal", "Choose the deal this quote is for");
      return;
    }
    setCreating(true);
    try {
      const res: any = await api.post("/api/Quotation/Insert", { deal_id: selectedDeal });
      success("Quote Created", "Draft quote created from the deal's products");
      setNewOpen(false);
      navigate(`/quotes/${res?.AddtionalData}`);
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create quote");
    } finally {
      setCreating(false);
    }
  };

  // A saved view, or the filters Ask the CRM hands over with ?apply=
  const applySaved = (c: any) => {
    setTab((TABS.some((t) => t.id === c.tab) ? c.tab : "ALL") as Tab);
    setSearch(c.search || "");
    quoteFilters.replace(c.filters || {});
    fieldFilters.setAll(c.fieldFilters);
    sorting.setSort(c.sort);
  };

  return (
    <div className="space-y-4">
      <ApplyFromUrl apply={applySaved} />
      <ViewsPanel
        title="Quotes"
        saved={{
          module: "quotes",
          getConfig: () => ({ tab, search, filters: quoteFilters.values, fieldFilters: fieldFilters.values, sort: sorting.sort }),
          apply: applySaved,
        }}
        subtitle="GST quotations"
        active={tab}
        onSelect={(k) => setTab(k as Tab)}
        groups={[
          {
            items: [
              { key: "ALL", label: "All quotes", count: counts.ALL },
              { key: "MINE_TO_APPROVE", label: "Needs my approval", count: counts.MINE_TO_APPROVE, tone: "warning", hidden: !canApprove },
              { key: "MINE", label: "My quotes", count: counts.MINE },
              { key: "EXPIRING", label: "Expiring this week", count: counts.EXPIRING, tone: "warning" },
            ],
          },
          {
            heading: "Status",
            items: TABS.filter((t) => !["ALL", "MINE_TO_APPROVE", "MINE", "EXPIRING"].includes(t.id)).map((t) => ({
              key: t.id,
              label: t.label,
              count: counts[t.id],
              tone: t.id === "PENDING_APPROVAL" ? ("warning" as const) : undefined,
            })),
          },
        ]}
        footer={
          stats && (
            <div className="mt-3 flex flex-col gap-1.5 px-2.5 text-[13px]">
              <div className="flex justify-between"><span className="text-muted-foreground">Sent</span><span className="font-mono font-semibold">{formatINRCompact(stats.sent_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Accepted</span><span className="font-mono font-semibold text-gold-ink">{formatINRCompact(stats.accepted_amount)}</span></div>
              {stats.acceptance_rate !== null && <div className="flex justify-between"><span className="text-muted-foreground">Acceptance rate</span><span className="font-mono font-semibold">{stats.acceptance_rate}%</span></div>}
            </div>
          )
        }
      />

      <PageHeader
        title={TABS.find((t) => t.id === tab)?.label === "All" ? "All quotes" : TABS.find((t) => t.id === tab)?.label || "Quotes"}
        summary={
          <>
            {list.total}
            {(counts.MINE_TO_APPROVE ?? 0) > 0 && <span className="text-warning"> · {counts.MINE_TO_APPROVE} to approve</span>}
            {(counts.EXPIRING ?? 0) > 0 && <span className="text-warning"> · {counts.EXPIRING} expiring this week</span>}
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={loadQuotes} disabled={loading}>
              <RefreshCw className={`size-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={exportQuotes} disabled={exporting}>
              Export
            </Button>
            {canCreate && (
              <Button size="sm" onClick={openNewQuote}>
                <Plus className="size-4 mr-1.5" />
                New quote
              </Button>
            )}
          </>
        }
      />

      {canApprove && biggest && (counts.MINE_TO_APPROVE ?? 0) > 0 && tab !== "MINE_TO_APPROVE" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-accent px-4 py-2.5 text-[13px] text-accent-foreground">
          <span>
            <strong>
              {counts.MINE_TO_APPROVE} {counts.MINE_TO_APPROVE === 1 ? "quote needs" : "quotes need"} your approval.
            </strong>{" "}
            The largest is {biggest.display_number} ({biggest.account?.name || "customer"}, {formatINRCompact(biggest.grand_total)}
            {Number(biggest.max_discount_pct) > 0 ? `, ${Number(biggest.max_discount_pct)}% discount` : ""}).
          </span>
          <Button size="sm" onClick={() => setTab("MINE_TO_APPROVE")}>
            Review now
          </Button>
        </div>
      )}

      <div className="space-y-3">
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: "Search by quote number, customer or deal..." }}
          fields={[
            { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(quoteOptions.owners, quoteOptions.total, user?.Id), defaultValue: "all" },
            { name: "date", label: "Quote date", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
            { name: "validity", label: "Validity", icon: FilterValidityIcon, type: "select", options: VALIDITY_OPTIONS, defaultValue: "all" },
          ]}
          values={quoteFilters.values}
          onChange={(name, value) => quoteFilters.set(name as keyof typeof QUOTE_FILTER_DEFAULTS, value)}
          onReset={quoteFilters.reset}
          quickKey="quotes"
          fieldFilters={{ fields: fieldDefs || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/Quotation/FilterValues" }}
          sort={{ fields: fieldDefs || [], value: sorting.sort, onChange: sorting.setSort }}
          shown={list.total}
          total={list.total}
          noun="quotes"
        />
      </div>

      <BulkBar count={sel.count} onClear={sel.clear}>
        {canEditInline && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
      </BulkBar>
      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="Quotation" ids={sel.ids} fields={QUOTE_BULK_FIELDS} onDone={() => { sel.clear(); list.reload(); }} />

      {/* Table */}
      {loading && quotes.length === 0 ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input type="checkbox" aria-label="Select all" checked={sel.allOn} onChange={sel.toggleAll} className="rounded border-border size-4 accent-primary cursor-pointer" />
                </TableHead>
                <TableHead>Quote</TableHead>
                <TableHead>Customer / Deal</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead className="text-right">Grand Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow
                  key={q.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/quotes/${q.id}`)}
                >
                  <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label="Select row" checked={sel.selected.has(q.id)} onChange={() => sel.toggle(q.id)} className="rounded border-border size-4 accent-primary cursor-pointer" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <FileText className="size-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm font-mono">{q.display_number}<UploadedTag source={q.source} /></div>
                        {Number(q.max_discount_pct) > 0 && (
                          <div className="text-[11px] text-muted-foreground">
                            Max discount {Number(q.max_discount_pct)}%
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{q.account?.name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{q.deal?.name}</div>
                  </TableCell>
                  <TableCell>
                    <span onClick={(e) => e.stopPropagation()}><OwnerCell list="Quotation" id={q.id} ownerId={q.owner_id} owner={q.owner} enabled={canEditInline && q.quote_status === "DRAFT"} onSaved={(row) => list.patchRow(q.id, row)} onConflict={list.reload} /></span>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(q.quote_date)}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap"><InlineCell list="Quotation" id={q.id} field="valid_until" kind="date" value={q.valid_until} display={formatDate(q.valid_until)} enabled={canEditInline && q.quote_status === "DRAFT"} onSaved={(row) => list.patchRow(q.id, row)} onConflict={list.reload} />
                  </TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">
                    {formatINR(q.grand_total)}
                  </TableCell>
                  <TableCell>
                    <QuoteStatusBadge status={q.quote_status} expired={q.is_expired} />
                  </TableCell>
                </TableRow>
              ))}
              {quotes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    {tab === "MINE_TO_APPROVE" ? (
                      <StateCard compact icon={CheckCheck} title="Nothing waiting for your approval" body="Quotes that need you will show up here." />
                    ) : (
                      <ListEmpty
                        noun="quotes" error={list.error} onRetry={list.reload}
                        icon={FileText}
                        filtered={!!search.trim() || tab !== "ALL" || quoteFilters.activeCount > 0 || fieldFilters.activeCount > 0}
                        onClear={() => {
                          setSearch("");
                          setTab("ALL");
                          quoteFilters.reset();
                          fieldFilters.reset();
                        }}
                        createLabel="+ New quote"
                        onCreate={canCreate ? () => setNewOpen(true) : undefined}
                        firstUse="Quotes are made from a deal's products."
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* New quote dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">New Quote</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Pick an open deal. Its products are copied into a draft quote you can edit.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Deal *</Label>
              <SearchSelect
                value={selectedDeal}
                selectedLabel={selectedDealLabel}
                endpoint="/api/Deal/List"
                extraParams={{ status: "OPEN" }}
                placeholder="Search open deals"
                getLabel={(d) => `${d.name}${d.account?.name ? ` · ${d.account.name}` : ""}`}
                onChange={(id, row) => {
                  setSelectedDeal(id);
                  setSelectedDealLabel(row ? `${row.name}${row.account?.name ? ` · ${row.account.name}` : ""}` : "");
                }}
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" className="sm:mr-auto" onClick={() => { setNewOpen(false); setUploadOpen(true); }}>
                Upload a PDF instead
              </Button>
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating || !selectedDeal}>
                {creating ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Create Draft
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <UploadDocumentDialog kind="quote" open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
